/**
 * useAdminApi - Standardized admin API hook for instant feedback
 * 
 * This hook provides:
 * 1. No-cache fetch wrapper that bypasses CDN caching
 * 2. Optimistic update helpers for instant UI feedback
 * 3. Consistent error handling
 * 
 * Usage:
 * const { adminFetch, optimisticAdd, optimisticUpdate, optimisticRemove } = useAdminApi<TwitterHandle>();
 * 
 * // Fetch with no caching
 * const data = await adminFetch('/api/twitter/handles');
 * 
 * // Create with optimistic update
 * const newHandle = await adminFetch('/api/twitter/handles', { method: 'POST', body: formData });
 * optimisticAdd(setHandles, newHandle);
 */

import { useCallback } from 'react';
import { getAuthHeaders } from '@/lib/auth-utils';

interface AdminFetchOptions extends Omit<RequestInit, 'body'> {
  body?: any;
}

interface UseAdminApiReturn<T> {
  /**
   * Fetch wrapper that ensures no caching for admin routes.
   * Automatically adds no-cache headers and parses JSON response.
   */
  adminFetch: <R = any>(url: string, options?: AdminFetchOptions) => Promise<R>;
  
  /**
   * Optimistically add an item to a list state
   */
  optimisticAdd: (
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    newItem: T
  ) => void;
  
  /**
   * Optimistically update an item in a list state by ID
   */
  optimisticUpdate: (
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    updatedItem: T,
    idField?: keyof T
  ) => void;
  
  /**
   * Optimistically remove an item from a list state by ID
   */
  optimisticRemove: (
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    itemId: string,
    idField?: keyof T
  ) => void;
}

/**
 * Hook for standardized admin API operations with instant feedback
 * 
 * @template T - The type of items being managed (e.g., TwitterHandle, Tag)
 */
export function useAdminApi<T extends { id?: string }>(): UseAdminApiReturn<T> {
  
  /**
   * Admin fetch wrapper that ensures no caching
   */
  const adminFetch = useCallback(async <R = any>(
    url: string,
    options: AdminFetchOptions = {}
  ): Promise<R> => {
    const { body, headers: customHeaders, ...restOptions } = options;

    // Attach Firebase ID token so server-side route handlers using
    // verifyAndGetUser/requireRole succeed. Throws if no user is signed in,
    // which surfaces as the right "Please sign in" error rather than a 401.
    const authHeaders = await getAuthHeaders();

    const response = await fetch(url, {
      ...restOptions,
      headers: {
        ...authHeaders,
        // Force bypass any caching layers
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...customHeaders,
      },
      // Client-side cache bypass
      cache: 'no-store',
      body: body ? JSON.stringify(body) : undefined,
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `Request failed: ${response.statusText}`);
    }
    
    return data as R;
  }, []);
  
  /**
   * Optimistically add an item to state for instant UI feedback
   */
  const optimisticAdd = useCallback((
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    newItem: T
  ) => {
    setter(prev => [...prev, newItem]);
  }, []);
  
  /**
   * Optimistically update an item in state
   */
  const optimisticUpdate = useCallback((
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    updatedItem: T,
    idField: keyof T = 'id' as keyof T
  ) => {
    setter(prev => prev.map(item => 
      item[idField] === updatedItem[idField] ? updatedItem : item
    ));
  }, []);
  
  /**
   * Optimistically remove an item from state
   */
  const optimisticRemove = useCallback((
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    itemId: string,
    idField: keyof T = 'id' as keyof T
  ) => {
    setter(prev => prev.filter(item => item[idField] !== itemId));
  }, []);
  
  return {
    adminFetch,
    optimisticAdd,
    optimisticUpdate,
    optimisticRemove,
  };
}

/**
 * Standalone admin fetch function for use outside React components
 * (e.g., in services or utility functions)
 */
export async function adminFetch<R = any>(
  url: string,
  options: AdminFetchOptions = {}
): Promise<R> {
  const { body, headers: customHeaders, ...restOptions } = options;

  const authHeaders = await getAuthHeaders();

  const response = await fetch(url, {
    ...restOptions,
    headers: {
      ...authHeaders,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...customHeaders,
    },
    cache: 'no-store',
    body: body ? JSON.stringify(body) : undefined,
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `Request failed: ${response.statusText}`);
  }
  
  return data as R;
}

export default useAdminApi;

